import React, { useState, useEffect } from 'react';
import { Smartphone, Check, X, Zap, Target, TrendingUp } from 'lucide-react';
import { useDemoLang } from './demoLang';

const MTN_YELLOW = '#FFCC00';
const MTN_BLUE = '#004990';

const TIERS = [
  {
    id: 'curious', name_fr: 'Curieux', name_en: 'Curious', price_eur: 5, price_fcfa: 3250,
    features_fr: ['1 enfant', 'Matières principales', '3 tuteurs Socratiques'],
    features_en: ['1 child', 'Core subjects', '3 Socratic tutors'],
    color: '#0F4C4C',
  },
  {
    id: 'explorer', name_fr: 'Explorateur', name_en: 'Explorer', price_eur: 10, price_fcfa: 6500,
    features_fr: ['3 enfants', 'Toutes les matières', 'Tous les tuteurs', 'Prépa examens'],
    features_en: ['3 children', 'All subjects', 'All personas', 'Test prep'],
    color: MTN_YELLOW, textDark: true, popular: true,
  },
  {
    id: 'scholar', name_fr: 'Érudit', name_en: 'Scholar', price_eur: 15, price_fcfa: 9750,
    features_fr: ['5 enfants', 'Plateforme complète', 'Compagnon Hôpital', 'Support prioritaire'],
    features_en: ['5 children', 'Full platform', 'Hospital Companion', 'Priority support'],
    color: MTN_BLUE,
  },
];

const MoMoModal = ({ tier, onClose, t, lang }) => {
  const [step, setStep] = useState('phone'); // phone → sent → confirmed → success
  const [phone, setPhone] = useState('+237 6');
  useEffect(() => {
    if (step === 'sent') { const id = setTimeout(() => setStep('confirmed'), 2200); return () => clearTimeout(id); }
    if (step === 'confirmed') { const id = setTimeout(() => setStep('success'), 1800); return () => clearTimeout(id); }
  }, [step]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="momo-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md overflow-hidden shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 flex items-center justify-between" style={{ background: MTN_YELLOW, color: MTN_BLUE }}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/40 flex items-center justify-center font-heading font-bold text-xs">MTN</div>
            <div>
              <div className="font-heading font-bold">MTN Mobile Money</div>
              <div className="text-[11px] opacity-80">{t('Paiement sécurisé', 'Secure payment')}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/40 flex items-center justify-center" data-testid="momo-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {step === 'phone' && (
            <>
              <div className="text-navy/70 text-sm">{t('Numéro MTN Mobile Money', 'MTN Mobile Money number')}</div>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-yellow-400 focus:outline-none font-body text-lg font-heading font-bold text-navy"
                data-testid="momo-phone" placeholder="+237 6XX XXX XXX" />
              <div className="rounded-2xl bg-cream p-3">
                <div className="text-navy/60 text-xs">{t('Montant', 'Amount')}</div>
                <div className="font-heading text-2xl font-bold text-navy">{tier.price_fcfa.toLocaleString('fr-FR')} FCFA</div>
                <div className="text-navy/60 text-xs">{tier.name_en} · {t('par mois', 'per month')}</div>
              </div>
              <button onClick={() => setStep('sent')}
                className="w-full h-14 rounded-full font-heading font-bold text-lg animate-pulse"
                style={{ background: MTN_YELLOW, color: MTN_BLUE }}
                data-testid="momo-request">
                {t('Envoyer la demande de paiement', 'Send payment request')}
              </button>
            </>
          )}
          {step === 'sent' && (
            <div className="text-center py-6 space-y-3" data-testid="momo-sent">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center animate-spin" style={{ borderColor: MTN_YELLOW, borderWidth: 4, borderStyle: 'solid', borderTopColor: 'transparent' }} />
              <div className="font-heading font-bold text-navy text-lg">
                {t(`Demande de ${tier.price_fcfa.toLocaleString('fr-FR')} FCFA envoyée`,
                   `Payment request of ${tier.price_fcfa.toLocaleString('en-US')} FCFA sent`)}
              </div>
              <div className="text-navy/70 text-sm">{t('Vérifiez votre téléphone', 'Check your phone')}</div>
            </div>
          )}
          {step === 'confirmed' && (
            <div className="text-center py-6 space-y-3" data-testid="momo-confirming">
              <button className="w-full h-14 rounded-full font-heading font-bold text-lg animate-pulse" style={{ background: MTN_YELLOW, color: MTN_BLUE }}>
                {t('Confirmer sur votre téléphone', 'Confirm on your phone')} 📱
              </button>
              <div className="text-navy/70 text-sm">{t('En attente de confirmation…', 'Waiting for confirmation…')}</div>
            </div>
          )}
          {step === 'success' && (
            <div className="text-center py-6 space-y-3" data-testid="momo-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-teal flex items-center justify-center animate-in zoom-in">
                <Check className="w-9 h-9 text-white" strokeWidth={3} />
              </div>
              <div className="font-heading font-bold text-navy text-xl">
                {t('Paiement confirmé ✅', 'Payment confirmed ✅')}
              </div>
              <div className="text-navy/70">
                {t(`Bienvenue dans OuiStuPaStu ${tier.name_fr} !`, `Welcome to OuiStuPaStu ${tier.name_en}!`)}
              </div>
              <button onClick={onClose} className="mt-2 h-11 px-6 rounded-full bg-navy text-white font-heading font-bold text-sm" data-testid="momo-done">
                {t('Commencer', 'Get started')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RevenueDonut = ({ t }) => {
  const app = 80, mtn = 20;
  const R = 55, cx = 70, cy = 70;
  const angle = (app / 100) * Math.PI * 2;
  const a0 = -Math.PI / 2;
  const x1 = cx + R * Math.cos(a0), y1 = cy + R * Math.sin(a0);
  const x2 = cx + R * Math.cos(a0 + angle), y2 = cy + R * Math.sin(a0 + angle);
  return (
    <section className="rounded-3xl bg-white p-5 border-2 border-cream shadow-squishy" data-testid="revenue-donut">
      <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2">
        {t('Partage des revenus', 'Revenue share')}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <svg viewBox="0 0 140 140" className="w-36 h-36" data-testid="revenue-chart">
          <circle cx={cx} cy={cy} r={R} fill={MTN_YELLOW} />
          <path d={`M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 1 1 ${x2} ${y2} Z`} fill={MTN_BLUE} />
          <circle cx={cx} cy={cy} r={26} fill="white" />
          <text x={cx} y={cy - 3} textAnchor="middle" fontSize="11" fontFamily="Fredoka" fill="#0F4C4C" fontWeight="bold">80 / 20</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill="#0F4C4C" opacity="0.6">app / MTN</text>
        </svg>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ background: MTN_BLUE }} />
            <span className="text-navy text-sm">OuiStuPaStu · 80%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ background: MTN_YELLOW }} />
            <span className="text-navy text-sm">MTN · 20%</span>
          </div>
          <div className="rounded-2xl p-3 text-white font-heading font-bold text-sm" style={{ background: MTN_YELLOW, color: MTN_BLUE }}>
            {t('MTN gagne 650 FCFA par abonnement Explorateur, chaque mois.',
               'MTN earns 650 FCFA per Explorer subscription, every month.')}
          </div>
          <div className="text-navy/70 text-xs">
            {t('À 100 000 abonnés = 65 000 000 FCFA / mois pour MTN.',
               'At 100,000 subscribers = 65,000,000 FCFA/month for MTN.')}
          </div>
        </div>
      </div>
    </section>
  );
};

const Counter = ({ target, prefix = '', suffix = '' }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start = performance.now();
    const dur = 2000;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setN(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <>{prefix}{n.toLocaleString('fr-FR')}{suffix}</>;
};

export default function Partnership() {
  const { lang, t } = useDemoLang();
  const [tierOpen, setTierOpen] = useState(null);

  return (
    <div className="space-y-6" data-testid="partnership-page">
      {/* Pricing cards */}
      <section data-testid="pricing-tiers">
        <div className="text-navy/60 font-heading font-bold text-[10px] uppercase tracking-wider mb-2 text-center">
          {t('Formules', 'Pricing tiers')}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TIERS.map((tier) => (
            <div key={tier.id} className={`rounded-3xl overflow-hidden shadow-squishyLg relative ${tier.popular ? 'ring-4' : ''}`}
              style={tier.popular ? { ['--tw-ring-color']: MTN_YELLOW } : {}}
              data-testid={`tier-${tier.id}`}>
              {tier.popular && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-heading font-bold" style={{ background: MTN_BLUE, color: 'white' }}>
                  {t('POPULAIRE', 'POPULAR')}
                </div>
              )}
              <div className="p-5" style={{ background: tier.color, color: tier.textDark ? MTN_BLUE : 'white' }}>
                <div className="font-heading text-lg font-bold">{lang === 'fr' ? tier.name_fr : tier.name_en}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-heading text-3xl font-bold">€{tier.price_eur}</span>
                  <span className="text-sm opacity-80">/{t('mois', 'mo')}</span>
                </div>
                <div className="text-[11px] opacity-80 mt-0.5">= {tier.price_fcfa.toLocaleString('fr-FR')} FCFA</div>
              </div>
              <div className="bg-white p-4">
                <ul className="space-y-1.5 mb-3">
                  {(lang === 'fr' ? tier.features_fr : tier.features_en).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-navy text-sm">
                      <Check className="w-4 h-4 text-teal shrink-0 mt-0.5" /><span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setTierOpen(tier)}
                  className="w-full h-12 rounded-full font-heading font-bold text-sm inline-flex items-center justify-center gap-2 shadow-lg hover:brightness-110 transition-all"
                  style={{ background: MTN_YELLOW, color: MTN_BLUE }}
                  data-testid={`subscribe-${tier.id}`}>
                  <Smartphone className="w-4 h-4" />
                  {t('Payer avec MTN MoMo', 'Pay with MTN MoMo')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Revenue share */}
      <RevenueDonut t={t} />

      {/* Market opportunity */}
      <section className="rounded-3xl p-5 text-white shadow-squishyLg" style={{ background: `linear-gradient(135deg, ${MTN_BLUE}, #0F4C4C)` }} data-testid="market-opportunity">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4" style={{ color: MTN_YELLOW }} />
          <span className="font-heading font-bold text-[10px] uppercase tracking-wider" style={{ color: MTN_YELLOW }}>
            {t('Opportunité de marché', 'Market opportunity')}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white/10 p-3">
            <div className="text-3xl">🇨🇲</div>
            <div className="font-heading text-2xl font-bold mt-1" data-testid="market-students"><Counter target={7300000} /></div>
            <div className="text-white/80 text-xs">{t('élèves au Cameroun', 'students in Cameroon')}</div>
          </div>
          <div className="rounded-2xl bg-white/10 p-3">
            <div className="text-3xl">🎯</div>
            <div className="font-heading text-2xl font-bold mt-1" data-testid="market-target">1 000 000</div>
            <div className="text-white/80 text-xs">{t("cible = 13,7% de part de marché", 'target = 13.7% market share')}</div>
          </div>
          <div className="rounded-2xl bg-white/10 p-3">
            <div className="text-3xl">💸</div>
            <div className="font-heading text-lg font-bold mt-1">10 000–16 900 FCFA</div>
            <div className="text-white/80 text-xs">{t("répétiteur privé / heure", 'private tutor / hour')}</div>
          </div>
          <div className="rounded-2xl p-3" style={{ background: MTN_YELLOW, color: MTN_BLUE }}>
            <div className="text-2xl">✅</div>
            <div className="font-heading text-lg font-bold mt-1">3 250 FCFA / {t('mois', 'mo')}</div>
            <div className="text-[11px] opacity-80">{t('OuiStuPaStu — illimité, tous les jours', 'OuiStuPaStu — unlimited, every day')}</div>
          </div>
        </div>
        <div className="mt-4 rounded-2xl bg-white/15 p-4 text-center">
          <div className="text-[10px] font-heading font-bold uppercase tracking-wider" style={{ color: MTN_YELLOW }}>
            {t('Revenu mensuel de la plateforme à cette cible', 'Monthly platform revenue at target')}
          </div>
          <div className="font-heading text-3xl font-bold mt-1 flex items-center justify-center gap-2" data-testid="market-revenue">
            <TrendingUp className="w-6 h-6" style={{ color: MTN_YELLOW }} />
            <Counter target={3250000000} /> FCFA
          </div>
        </div>
        <p className="mt-4 text-white text-center font-heading font-bold text-lg leading-tight">
          "{t('Chaque enfant mérite un grand tuteur. Nous rendons cela possible.',
              'Every child deserves a great tutor. We make it possible.')}"
        </p>
      </section>

      {tierOpen && <MoMoModal tier={tierOpen} onClose={() => setTierOpen(null)} t={t} lang={lang} />}
    </div>
  );
}
